import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Phone, Shield, CheckCircle2 } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { buildScheme } from '../DeepLinkUtils';
import { MOBILE_APP_PAUSED } from '../../constants/appStore';
import { supabase } from '../../lib/supabase';

export default function SmsConsent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const companyId = searchParams.get('companyId') || '';
  const locationId = searchParams.get('locationId') || '';
  
  const [phoneNumber, setPhoneNumber] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Detect mobile and redirect to app — skipped while MOBILE_APP_PAUSED so users
  // complete SMS consent on the web instead of bouncing off a delisted app.
  React.useEffect(() => {
    if (MOBILE_APP_PAUSED) return;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      const params = new URLSearchParams();
      if (email) params.set('email', email);
      if (companyId) params.set('companyId', companyId);
      if (locationId) params.set('locationId', locationId);

      const mobileDeepLink = buildScheme(`/sms-consent?${params.toString()}`);
      window.location.replace(mobileDeepLink);
    }
  }, [email, companyId, locationId]);

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX for US numbers
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
    setError(null);
  };

  const validatePhoneNumber = (phone: string): boolean => {
    // Remove formatting to check digits
    const digits = phone.replace(/\D/g, '');
    // US phone numbers should be 10 digits
    return digits.length === 10;
  };

  const handleSubmit = async () => {
    if (!phoneNumber) {
      setError('Phone number is required');
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    if (!consentGiven) {
      setError('You must consent to receive SMS messages');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Format phone number for Supabase (E.164 format: +1XXXXXXXXXX)
      const digits = phoneNumber.replace(/\D/g, '');
      const formattedPhone = `+1${digits}`;

      // Send OTP via Supabase (will use Twilio in production)
      const { error: otpError } = await supabase.auth.signInWithOtp({
        phone: formattedPhone,
        options: {
          shouldCreateUser: true, // Allow creating new users via phone
          data: {
            signup_intent: email ? 'draft_student_invite' : 'student_signup',
            ...(email && { email }),
            ...(companyId && { company_id: companyId }),
            ...(locationId && { location_id: locationId }),
          },
        },
      });

      if (otpError) {
        setError(otpError.message || 'Failed to send verification code. Please try again.');
        return;
      }

      // Success - redirect to verify OTP screen with phone number
      const params = new URLSearchParams();
      params.set('phone', formattedPhone);
      params.set('type', 'sms');
      if (email) params.set('email', email);
      if (companyId) params.set('companyId', companyId);
      if (locationId) params.set('locationId', locationId);
      
      navigate(`/verify-otp?${params.toString()}`);
    } catch {
      setError('Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111010] flex items-center justify-center p-4">
      <Card className="w-full max-w-sm sm:max-w-md border-[#504A4A] bg-[#221F1F]">
        <CardHeader>
          <CardTitle className="text-white text-center text-lg sm:text-xl flex items-center justify-center gap-2">
            <Phone className="w-5 h-5" />
            SMS Verification
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-6">
          <div className="text-center">
            <p className="text-gray-300 mb-2 text-sm sm:text-base">
              We&apos;ll send you a verification code via SMS to complete your signup.
            </p>
            {email && (
              <p className="text-gray-400 text-xs sm:text-sm mb-4 break-all">
                Email: <span className="font-mono">{email}</span>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="phone" className="text-xs sm:text-sm font-medium text-gray-300">
              Phone Number
            </label>
            <Input
              id="phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={phoneNumber}
              onChange={handlePhoneChange}
              disabled={loading}
              className="w-full text-sm sm:text-base px-3 sm:px-4 py-2 sm:py-2.5 bg-[#504A4A] border-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3]"
              maxLength={14} // (XXX) XXX-XXXX format
            />
            <p className="text-xs text-gray-500">
              Enter your 10-digit US phone number
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3 p-4 bg-[#1a1a1a] rounded-lg border border-gray-700">
              <Shield className="w-5 h-5 text-[#00D9FF] mt-0.5 flex-shrink-0" />
              <div className="flex-1 text-sm text-gray-300">
                <p className="font-medium mb-1">SMS Consent</p>
                <p className="text-gray-400">
                  By providing your phone number, you consent to receive SMS messages from RollOps for account verification and important updates. Message and data rates may apply. You can opt out at any time by replying STOP.{' '}
                  <a
                    href="/sms-consent-legal"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00D9FF] hover:underline"
                  >
                    View full SMS consent terms
                  </a>
                  .
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setConsentGiven(!consentGiven)}
              className="w-full flex items-center gap-3 p-4 bg-[#1a1a1a] rounded-lg border border-gray-700 hover:border-[#00D9FF] transition-colors text-left"
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                consentGiven 
                  ? 'bg-[#00D9FF] border-[#00D9FF]' 
                  : 'border-gray-600'
              }`}>
                {consentGiven && <CheckCircle2 className="w-4 h-4 text-white" />}
              </div>
              <span className="text-sm text-gray-300">
                I consent to receive SMS messages for verification
              </span>
            </button>
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm text-center">{error}</p>
            </div>
          )}

          <Button
            onClick={handleSubmit}
            disabled={loading || !phoneNumber || !consentGiven || !validatePhoneNumber(phoneNumber)}
            className="w-full bg-[#e11d48] hover:bg-[#be1e3e] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Sending Code...' : 'Send Verification Code'}
          </Button>

          <div className="text-center">
            <button
              onClick={() => navigate('/enter-invite-code' + (email ? `?email=${encodeURIComponent(email)}` : ''))}
              className="text-sm text-[#00D9FF] hover:text-[#00B8D9]"
            >
              Use email verification instead
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}







